import React, { createContext, useContext, useState, useEffect } from 'react';
import { products as initialProducts, categories as initialCategories, textures as initialTextures } from '../data/products';
import { productsAPI, categoriesAPI, texturesAPI, imagesAPI } from '../services/api';

import Alys_80b from '../assets/images/sides/Alys_80b.png';
import Bianca97 from '../assets/images/sides/Bianca97.png';
import Bianca111b from '../assets/images/sides/Bianca111b.png';
import Kris_36c from '../assets/images/sides/Kris_36c.png';
import Kris_33a from '../assets/images/sides/Kris_33a.png';
import Rico_1b from '../assets/images/sides/Rico_1b.png';

const AdminContext = createContext();

const defaultSideImages = {
  Alys_80b,
  Bianca97,
  Bianca111b,
  Kris_36c,
  Kris_33a,
  Rico_1b
};

const defaultSectionImages = {
  veloura: {
    about: { left: Alys_80b, right: Bianca97 },
    studios: { left: Bianca111b, right: Kris_36c },
    vague: { left: Kris_33a, right: Rico_1b },

    careers: { left: Alys_80b, right: Bianca111b },
    partners: { left: Bianca111b, right: Kris_36c },
    news: { left: Kris_33a, right: Rico_1b },

    // Legacy fallback for older saved admin data.
    opportunities: { left: Alys_80b, right: Bianca111b }
  },

  vague: {
    about: { left: Kris_33a, right: Rico_1b },
    studios: { left: Alys_80b, right: Bianca97 },
    vague: { left: Bianca111b, right: Kris_36c },
    opportunities: { left: Kris_36c, right: Alys_80b }
  }
};

const mergeBrandSections = (defaultBrandSections, savedBrandSections = {}) => {
  const merged = {};

  Object.keys(defaultBrandSections).forEach((sectionKey) => {
    merged[sectionKey] = {
      ...defaultBrandSections[sectionKey],
      ...(savedBrandSections[sectionKey] || {})
    };
  });

  Object.keys(savedBrandSections).forEach((sectionKey) => {
    if (!merged[sectionKey]) {
      merged[sectionKey] = savedBrandSections[sectionKey];
    }
  });

  return merged;
};

const normalizeSectionImages = (savedImages = {}) => {
  const savedVeloura = savedImages.veloura || {};
  const savedVague = savedImages.vague || {};
  const legacyVelouraOpportunities = savedVeloura.opportunities || {};

  const veloura = mergeBrandSections(defaultSectionImages.veloura, savedVeloura);

  veloura.careers = {
    ...defaultSectionImages.veloura.careers,
    ...legacyVelouraOpportunities,
    ...(savedVeloura.careers || {})
  };

  veloura.partners = {
    ...defaultSectionImages.veloura.partners,
    ...legacyVelouraOpportunities,
    ...(savedVeloura.partners || {})
  };

  veloura.news = {
    ...defaultSectionImages.veloura.news,
    ...legacyVelouraOpportunities,
    ...(savedVeloura.news || {})
  };

  return {
    veloura,
    vague: mergeBrandSections(defaultSectionImages.vague, savedVague)
  };
};

export const AdminProvider = ({ children }) => {
  const [adminProducts, setAdminProducts] = useState([]);
  const [adminCategories, setAdminCategories] = useState([]);
  const [adminTextures, setAdminTextures] = useState([]);
  const [sideImages, setSideImages] = useState(defaultSideImages);
  const [sectionImages, setSectionImages] = useState(defaultSectionImages);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [productsResponse, categoriesResponse, texturesResponse, imagesResponse] = await Promise.all([
        productsAPI.getAll().catch(() => ({ success: false, data: initialProducts })),
        categoriesAPI.getAll().catch(() => ({ success: false, data: initialCategories })),
        texturesAPI.getAll().catch(() => ({ success: false, data: Object.values(initialTextures) })),
        imagesAPI.getLandingImages().catch(() => ({ success: false, data: defaultSectionImages }))
      ]);

      if (productsResponse.success) {
        setAdminProducts(productsResponse.data);
      } else {
        setAdminProducts(initialProducts);
      }

      if (categoriesResponse.success) {
        setAdminCategories(categoriesResponse.data);
      } else {
        setAdminCategories(initialCategories);
      }

      if (texturesResponse.success) {
        if (Array.isArray(texturesResponse.data)) {
          setAdminTextures(texturesResponse.data);
        } else {
          const texturesArray = Object.entries(texturesResponse.data || {}).map(([id, data]) => ({
            id,
            ...data
          }));
          setAdminTextures(texturesArray);
        }
      } else {
        const texturesArray = Object.entries(initialTextures).map(([id, data]) => ({
          id,
          ...data
        }));
        setAdminTextures(texturesArray);
      }

      if (imagesResponse.success && imagesResponse.data) {
        setSectionImages(normalizeSectionImages(imagesResponse.data));
      } else {
        setSectionImages(defaultSectionImages);
      }

      setIsInitialized(true);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);

      setAdminProducts(initialProducts);
      setAdminCategories(initialCategories);

      const texturesArray = Object.entries(initialTextures).map(([id, data]) => ({
        id,
        ...data
      }));

      setAdminTextures(texturesArray);
      setSectionImages(defaultSectionImages);
      setIsInitialized(true);
    } finally {
      setLoading(false);
    }
  };

  const addProduct = async (product) => {
    try {
      const response = await productsAPI.create(product);

      if (response.success) {
        await loadData();
        return response.data;
      }

      throw new Error(response.message || 'Failed to create product');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateProduct = async (id, updates) => {
    try {
      const response = await productsAPI.update(id, updates);

      if (response.success) {
        await loadData();
        return response.data;
      }

      throw new Error(response.message || 'Failed to update product');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const deleteProduct = async (id) => {
    try {
      const response = await productsAPI.delete(id);

      if (response.success) {
        await loadData();
        return true;
      }

      throw new Error(response.message || 'Failed to delete product');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const reorderProducts = async (startIndex, endIndex) => {
    const result = Array.from(adminProducts);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    setAdminProducts(result);

    try {
      const productIds = result.map((product) => product.id);
      const response = await productsAPI.reorder(productIds);

      if (!response.success) {
        await loadData();
        throw new Error(response.message || 'Failed to reorder products');
      }
    } catch (err) {
      await loadData();
      setError(err.message);
      throw err;
    }
  };

  const addCategory = async (category) => {
    try {
      const response = await categoriesAPI.create(category);

      if (response.success) {
        await loadData();
        return response.data;
      }

      throw new Error(response.message || 'Failed to create category');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateCategory = async (id, updates) => {
    try {
      const response = await categoriesAPI.update(id, updates);

      if (response.success) {
        await loadData();
        return response.data;
      }

      throw new Error(response.message || 'Failed to update category');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const deleteCategory = async (id) => {
    if (id === 'all') {
      throw new Error('Cannot delete the "all" category');
    }

    try {
      const response = await categoriesAPI.delete(id);

      if (response.success) {
        await loadData();
        return true;
      }

      throw new Error(response.message || 'Failed to delete category');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const reorderCategories = async (startIndex, endIndex) => {
    const result = Array.from(adminCategories);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    setAdminCategories(result);

    try {
      const categoryIds = result.map((category) => category.id);
      const response = await categoriesAPI.reorder(categoryIds);

      if (!response.success) {
        await loadData();
        throw new Error(response.message || 'Failed to reorder categories');
      }
    } catch (err) {
      await loadData();
      setError(err.message);
      throw err;
    }
  };

  const addTexture = async (texture) => {
    try {
      const response = await texturesAPI.create(texture);

      if (response.success) {
        await loadData();
        return response.data;
      }

      throw new Error(response.message || 'Failed to create texture');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateTexture = async (id, updates) => {
    try {
      const response = await texturesAPI.update(id, updates);

      if (response.success) {
        await loadData();
        return response.data;
      }

      throw new Error(response.message || 'Failed to update texture');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const deleteTexture = async (id) => {
    try {
      const response = await texturesAPI.delete(id);

      if (response.success) {
        await loadData();
        return true;
      }

      throw new Error(response.message || 'Failed to delete texture');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateSectionImage = async (brand, section, side, imageUrl) => {
    setSectionImages((prev) => ({
      ...prev,
      [brand]: {
        ...(prev[brand] || {}),
        [section]: {
          ...(prev[brand]?.[section] || {}),
          [side]: imageUrl
        }
      }
    }));

    try {
      const response = await imagesAPI.updateLandingImage(brand, section, side, imageUrl);

      if (!response.success) {
        await loadData();
        throw new Error(response.message || 'Failed to update image');
      }
    } catch (err) {
      await loadData();
      setError(err.message);
      throw err;
    }
  };

  const resetToDefaults = async () => {
    setAdminProducts(initialProducts);
    setAdminCategories(initialCategories);
    setSideImages(defaultSideImages);
    setSectionImages(defaultSectionImages);

    await loadData();
  };

  const value = {
    products: adminProducts,
    categories: adminCategories,
    textures: adminTextures,
    sideImages,
    sectionImages,
    addProduct,
    updateProduct,
    deleteProduct,
    reorderProducts,
    addCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,
    addTexture,
    updateTexture,
    deleteTexture,
    updateSectionImage,
    resetToDefaults,
    isInitialized,
    loading,
    error,
    refreshData: loadData
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);

  if (!context) {
    return {
      products: [],
      categories: [],
      textures: [],
      sideImages: defaultSideImages,
      sectionImages: defaultSectionImages,
      addProduct: () => {},
      updateProduct: () => {},
      deleteProduct: () => {},
      reorderProducts: () => {},
      addCategory: () => {},
      updateCategory: () => {},
      deleteCategory: () => {},
      reorderCategories: () => {},
      addTexture: () => {},
      updateTexture: () => {},
      deleteTexture: () => {},
      updateSectionImage: () => {},
      resetToDefaults: () => {},
      isInitialized: false,
      loading: false,
      error: null,
      refreshData: () => {}
    };
  }

  return context;
};
